// Canvas 2D "hologram" built from a real listing photo. No external libraries.
const TINT = { a: [95, 227, 255], b: [180, 140, 255] };
const clamp = (x) => Math.min(1, Math.max(0, x));
const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

const ICE = [215, 238, 255];

export function holoBitmap(img, tintRgb) {
  const rgb = ICE.map((c, i) => c * 0.7 + tintRgb[i] * 0.3);
  const w = 900;
  const h = Math.max(1, Math.round((img.naturalHeight * w) / img.naturalWidth));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  try {
    const d = ctx.getImageData(0, 0, w, h).data;
    const lum = new Float32Array(w * h);
    for (let i = 0; i < w * h; i++) lum[i] = (0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2]) / 255;
    const out = ctx.createImageData(w, h);
    const o = out.data;
    const R = (x, y) => lum[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
    const sm = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let s = 0;
        for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) s += R(x + i, y + j);
        sm[y * w + x] = s / 9;
      }
    }
    const L = (x, y) => sm[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
    for (let y = 0; y < h; y++) {
      const dy = (y - h * 0.55) / (h * 0.55);
      for (let x = 0; x < w; x++) {
        const gx = -L(x - 1, y - 1) - 2 * L(x - 1, y) - L(x - 1, y + 1) + L(x + 1, y - 1) + 2 * L(x + 1, y) + L(x + 1, y + 1);
        const gy = -L(x - 1, y - 1) - 2 * L(x, y - 1) - L(x + 1, y - 1) + L(x - 1, y + 1) + 2 * L(x, y + 1) + L(x + 1, y + 1);
        const q = clamp((Math.sqrt(gx * gx + gy * gy) - 0.12) / 0.45);
        const e = q * q * (3 - 2 * q);
        const l = lum[y * w + x];
        const v = Math.min(1, 0.45 + 0.4 * l + 1.6 * e);
        const dx = (x - w / 2) / (w * 0.5);
        const mask = clamp((1 - (dx * dx + dy * dy)) / 0.45);
        const k = (y * w + x) * 4;
        o[k] = Math.min(255, rgb[0] * v + 60 * e);
        o[k + 1] = Math.min(255, rgb[1] * v + 60 * e);
        o[k + 2] = Math.min(255, rgb[2] * v + 60 * e);
        o[k + 3] = 255 * Math.min(1, 0.03 + 0.16 * l * l + 1.5 * e) * mask;
      }
    }
    ctx.putImageData(out, 0, 0);
  } catch {
    ctx.clearRect(0, 0, w, h);
    ctx.filter = "grayscale(1) contrast(1.4) brightness(1.2)";
    ctx.drawImage(img, 0, 0, w, h);
    ctx.filter = "none";
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = `rgb(${rgb.join(",")})`;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "destination-in";
    const g = ctx.createRadialGradient(w / 2, h * 0.55, Math.min(w, h) * 0.2, w / 2, h * 0.55, w * 0.5);
    g.addColorStop(0, "rgba(0,0,0,1)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = "source-over";
  }
  const b = document.createElement("canvas");
  b.width = w;
  b.height = h;
  const bx = b.getContext("2d");
  bx.filter = "blur(6px)";
  bx.globalAlpha = 0.5;
  bx.drawImage(c, 0, 0);
  bx.filter = "none";
  bx.globalAlpha = 1;
  bx.globalCompositeOperation = "lighter";
  bx.drawImage(c, 0, 0);
  return b;
}

const loadImg = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

export function mountPhotoHologram(host, { photos, view = 0, reducedMotion = false, onError }) {
  const canvas = document.createElement("canvas");
  canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%";
  host.appendChild(canvas);
  const ctx = canvas.getContext("2d");
  let W = 1, H = 1, dpr = 1, raf = 0, disposed = false, bms = null;
  let start = performance.now(), cur = view, prev = view, switchAt = -1e9;

  const resize = () => {
    dpr = Math.min(2, window.devicePixelRatio || 1);
    W = host.clientWidth || 1;
    H = host.clientHeight || 1;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  };
  const ro = new ResizeObserver(resize);
  ro.observe(host);
  resize();

  Promise.all([loadImg(photos.a), loadImg(photos.b)]).then((imgs) => {
    if (disposed) return;
    bms = [holoBitmap(imgs[0], TINT.a), holoBitmap(imgs[1], TINT.b)];
    start = performance.now();
    raf = requestAnimationFrame(frame);
  }).catch(() => { if (!disposed) onError?.(); });

  const tint = (i, a) => `rgba(${(i === 0 ? TINT.a : TINT.b).join(",")},${a})`;
  const fit = (bm, width) => {
    let dw = width, dh = (width * bm.height) / bm.width;
    if (dh > H * 0.72) { dh = H * 0.72; dw = (dh * bm.width) / bm.height; }
    return [dw, dh];
  };
  const drawCar = (i, cx, width, alpha, floorY) => {
    if (alpha <= 0) return null;
    const [dw, dh] = fit(bms[i], width);
    ctx.globalAlpha = alpha;
    ctx.drawImage(bms[i], cx - dw / 2, floorY - dh * 0.96, dw, dh);
    ctx.globalAlpha = 1;
    return [cx - dw / 2, floorY - dh * 0.96, dw, dh];
  };

  function frame(now) {
    if (disposed) return;
    const t = reducedMotion ? 1e9 : now - start;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    ctx.globalCompositeOperation = "lighter";
    const narrow = W < 760 || H > W;
    const finalW = W * (narrow ? 0.92 : 0.68);
    const sepW = W * (narrow ? 0.46 : 0.4);
    const bob = t > 3000 ? Math.sin(t / 900) * 4 : 0;
    const floorY = H * 0.82;

    for (let r = 0; r < 3; r++) {
      ctx.beginPath();
      ctx.ellipse(W / 2, floorY + 6, finalW * (0.56 - r * 0.12), H * (0.06 - r * 0.012), 0, 0, Math.PI * 2);
      ctx.strokeStyle = tint(cur, 0.5 - r * 0.12);
      ctx.lineWidth = 1.5;
      ctx.shadowColor = tint(cur, 0.9);
      ctx.shadowBlur = 14;
      ctx.stroke();
    }
    ctx.shadowBlur = 0;

    if (t < 2200) {
      const p = t < 700 ? 0 : ease((t - 700) / 1500);
      const width = sepW + (finalW - sepW) * p;
      drawCar(0, W * 0.27 + (W * 0.5 - W * 0.27) * p, width, 1, floorY);
      drawCar(1, W * 0.73 + (W * 0.5 - W * 0.73) * p, width, 1, floorY);
    } else if (t < 3000) {
      const q = (t - 2200) / 800;
      drawCar(0, W / 2, finalW, 1 - q, floorY);
      drawCar(1, W / 2, finalW, 1 - q, floorY);
      drawCar(cur, W / 2, finalW, q, floorY);
    } else {
      const flicker = reducedMotion ? 1 : 0.9 + 0.06 * Math.sin(t / 70) + 0.04 * Math.sin(t / 23);
      const k = reducedMotion ? 1 : clamp((now - switchAt) / 450);
      if (k < 1) drawCar(prev, W / 2, finalW, (1 - k) * flicker, floorY + bob);
      const rect = drawCar(cur, W / 2, finalW, k * flicker, floorY + bob);
      if (rect && !reducedMotion) {
        const [x, y, w, h] = rect;
        const sy = y + ((t % 2600) / 2600) * h;
        const oy = (sy - (y + h * 0.55)) / (h * 0.55);
        const half = w * 0.42 * Math.sqrt(Math.max(0, 1 - oy * oy));
        if (half > 4) {
          const g = ctx.createLinearGradient(0, sy - 18, 0, sy + 18);
          g.addColorStop(0, tint(cur, 0));
          g.addColorStop(0.5, tint(cur, 0.35));
          g.addColorStop(1, tint(cur, 0));
          ctx.save();
          ctx.beginPath();
          ctx.ellipse(x + w / 2, sy, half, 18, 0, 0, Math.PI * 2);
          ctx.clip();
          ctx.fillStyle = g;
          ctx.fillRect(x + w / 2 - half, sy - 18, half * 2, 36);
          ctx.restore();
        }
      }
    }
    ctx.globalCompositeOperation = "source-over";
    raf = requestAnimationFrame(frame);
  }

  return {
    setView(v) { if (v === cur) return; prev = cur; cur = v; switchAt = performance.now(); },
    replay() { start = performance.now(); switchAt = -1e9; },
    dispose() { disposed = true; cancelAnimationFrame(raf); ro.disconnect(); canvas.remove(); },
  };
}
